import { Box, styled } from '@mui/material'
import { tokens } from 'ComponentLibrary'
import { MouseEventHandler, ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { TextWithEllipsis } from 'ComponentLibrary/src/piazza/text'
import { Typography } from '..'

type BaseLinkProps = {
    label?: string
    startIcon?: ReactNode
    endIcon?: ReactNode
    caps?: boolean
    selected?: boolean
    disableHover?: boolean
}

type LinkProps = BaseLinkProps & {
    href: string
}

export function Link({ label = '', startIcon, endIcon, href, caps, ellipsis, selected }: LinkProps & { ellipsis?: boolean }) {
    const TextContent = ellipsis ? (
        <TextWithEllipsis typographyProps={{ variant: caps ? 'linkCaps' : 'link' }} tooltipText={label}>
            {label}
        </TextWithEllipsis>
    ) : (
        <Typography variant={caps ? 'linkCaps' : 'link'}>{label}</Typography>
    )

    return (
        <StyledLink $selected={selected} href={href} onClick={(e) => e.stopPropagation()}>
            {startIcon && <IconContainer $selected={selected}>{startIcon}</IconContainer>}
            {label && TextContent}
            {endIcon && <IconContainer $selected={selected}>{endIcon}</IconContainer>}
        </StyledLink>
    )
}

export function NavLink({
    label,
    startIcon,
    endIcon,
    href,
    caps,
    selected,
    forceReload,
    breadcrumbs,
    disableHover,
    secondary,
}: LinkProps & { forceReload?: boolean; breadcrumbs?: boolean; secondary?: boolean }) {
    const iconSize = breadcrumbs ? tokens.SizingIconS : tokens.SizingIconM
    const variant = breadcrumbs ? 'headline5' : caps ? 'linkCaps' : secondary ? 'textS' : 'link'
    return (
        <StyledNavLink $selected={selected} to={href} reloadDocument={forceReload} $disableHover={disableHover}>
            {startIcon && (
                <IconContainer $selected={selected} $iconSize={iconSize} $disableHover={disableHover}>
                    {startIcon}
                </IconContainer>
            )}
            {label && (
                <TextWithEllipsis tooltipText={label} placement="right" typographyProps={{ variant, noWrap: breadcrumbs }}>
                    {label}
                </TextWithEllipsis>
            )}
            {endIcon && (
                <IconContainer $selected={selected} $iconSize={tokens.SizingIconS} $disableHover={disableHover}>
                    {endIcon}
                </IconContainer>
            )}
        </StyledNavLink>
    )
}

export function ActionNavLink({
    label,
    startIcon,
    endIcon,
    caps,
    selected,
    onClick,
    breadcrumbs,
    disableHover,
    secondary,
}: BaseLinkProps & {
    onClick: MouseEventHandler<HTMLButtonElement>
    breadcrumbs?: boolean
    secondary?: boolean
}) {
    const iconSize = breadcrumbs ? tokens.SizingIconS : tokens.SizingIconM
    const variant = breadcrumbs ? 'headline5' : caps ? 'linkCaps' : secondary ? 'textS' : 'link'
    return (
        <StyledActionButton type="button" onClick={onClick} $selected={selected} $disableHover={disableHover}>
            {startIcon && (
                <IconContainer $selected={selected} $iconSize={iconSize} $disableHover={disableHover}>
                    {startIcon}
                </IconContainer>
            )}
            {label && (
                <TextWithEllipsis tooltipText={label} placement="right" typographyProps={{ variant, noWrap: breadcrumbs }}>
                    {label}
                </TextWithEllipsis>
            )}
            {endIcon && (
                <IconContainer $selected={selected} $iconSize={tokens.SizingIconS} $disableHover={disableHover}>
                    {endIcon}
                </IconContainer>
            )}
        </StyledActionButton>
    )
}

const linkStyles = ({ $selected, $disableHover }: { $selected?: boolean; $disableHover?: boolean }) => ({
    display: 'inline-flex',
    gap: tokens.SizingSpacing100,
    alignItems: 'center',
    cursor: 'pointer',
    width: '100%',
    textDecoration: 'none',
    color: $selected ? tokens.ColorTextLinkSelected : tokens.ColorTextLinkDefault,
    ...(!$disableHover && {
        '&:hover': {
            color: tokens.ColorTextLinkHover,
        },
    }),
})

const StyledLink = styled('a', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $selected?: boolean }>(({ $selected }) => linkStyles({ $selected }))

const StyledNavLink = styled(RouterLink, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $selected?: boolean; $disableHover?: boolean }>(({ $selected, $disableHover }) => linkStyles({ $selected, $disableHover }))

const StyledActionButton = styled('button', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $selected?: boolean; $disableHover?: boolean }>(({ $selected, $disableHover }) => ({
    ...linkStyles({ $selected, $disableHover }),
    padding: 0,
    border: 0,
    background: 'transparent',
    font: 'inherit',
}))

const IconContainer = styled(Box, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $selected?: boolean; $iconSize?: string; $disableHover?: boolean }>(({ $selected, $iconSize, $disableHover }) => ({
    fontSize: $iconSize || tokens.SizingIconM,
    color: $selected ? tokens.ColorIconLinkSelected : tokens.ColorIconLinkDefault,
    ...(!$disableHover && {
        '*:hover > &': {
            color: tokens.ColorIconLinkHover,
        },
    }),
    display: 'flex',
    height: $iconSize || tokens.SizingIconM,
}))
